/**
 * PartenariatStatsPanel.js — V37.12
 *
 * Mini tableau de bord pour la page « Demandes de Partenariat » (Super Admin).
 * Affiche :
 *   - Taux de complétion moyen
 *   - Longueur moyenne du message
 *   - Total + répartition par statut (résumé)
 *   - Top pays d'origine
 *   - Courbe d'activité sur 30 jours
 *
 * Endpoint backend : GET /api/demandes-partenariat/stats
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Type, Globe2, BarChart3, Loader2, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mapping minimal des codes pays → drapeau emoji (les plus probables d'apparaître).
const PAYS_FLAG = {
  FR: '🇫🇷', BE: '🇧🇪', CH: '🇨🇭', LU: '🇱🇺', MC: '🇲🇨', GP: '🇬🇵', MQ: '🇲🇶',
  GF: '🇬🇫', RE: '🇷🇪', YT: '🇾🇹', NC: '🇳🇨', PF: '🇵🇫', US: '🇺🇸', GB: '🇬🇧',
  DE: '🇩🇪', ES: '🇪🇸', IT: '🇮🇹', PT: '🇵🇹', NL: '🇳🇱', CA: '🇨🇦', '??': '🌍',
};

const formatShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

export default function PartenariatStatsPanel({ getAuthHeader, refreshKey }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/demandes-partenariat/stats`, {
          headers: getAuthHeader ? getAuthHeader() : {},
        });
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setError('Impossible de charger le tableau de bord.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader, refreshKey]);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="partenariat-stats-loading">
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Chargement du tableau de bord…</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card className="border-red-200 bg-red-50/50" data-testid="partenariat-stats-error">
        <CardContent className="py-6 flex items-center gap-3 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error || 'Données indisponibles.'}</span>
        </CardContent>
      </Card>
    );
  }

  const tauxPct = Math.round((stats.taux_completion_moyen || 0) * 100);
  const maxDaily = Math.max(1, ...(stats.daily_30d || []).map(d => d.count));
  const last7 = (stats.daily_30d || []).slice(-7).reduce((s, d) => s + d.count, 0);
  const prev7 = (stats.daily_30d || []).slice(-14, -7).reduce((s, d) => s + d.count, 0);
  const trend = prev7 === 0 ? (last7 > 0 ? '+∞' : '0') : `${last7 >= prev7 ? '+' : ''}${Math.round(((last7 - prev7) / prev7) * 100)}%`;
  const trendColor = last7 > prev7 ? 'text-emerald-600' : last7 < prev7 ? 'text-red-600' : 'text-slate-500';

  return (
    <div className="space-y-4" data-testid="partenariat-stats-panel">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Taux de complétion moyen */}
        <Card className="border-slate-200 overflow-hidden" data-testid="kpi-completion-rate">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                <BarChart3 className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Sur 9 champs
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {tauxPct}<span className="text-lg text-slate-400 ml-0.5">%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Taux de complétion moyen</p>
            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${tauxPct >= 80 ? 'bg-emerald-500' : tauxPct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${tauxPct}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Longueur moyenne du message */}
        <Card className="border-slate-200 overflow-hidden" data-testid="kpi-message-length">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                <Type className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Caractères
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {stats.longueur_message_moyenne}
            </div>
            <p className="text-xs text-slate-500 mt-1">Longueur moyenne du message</p>
            <p className="text-[11px] text-slate-400 mt-2">
              Minimum requis : <span className="font-medium text-slate-600">50 car.</span>
            </p>
          </CardContent>
        </Card>

        {/* Pays d'origine */}
        <Card className="border-slate-200 overflow-hidden" data-testid="kpi-countries">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                <Globe2 className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Top {stats.pays_top?.length || 0}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-2 font-medium uppercase tracking-wider">Pays d'origine</p>
            <div className="space-y-1.5" data-testid="kpi-countries-list">
              {(stats.pays_top || []).slice(0, 4).map((p) => {
                const total = stats.total || 1;
                const pct = Math.round((p.count / total) * 100);
                return (
                  <div key={p.code} className="flex items-center gap-2">
                    <span className="text-base leading-none">{PAYS_FLAG[p.code] || PAYS_FLAG['??']}</span>
                    <span className="text-xs font-medium text-slate-700 w-8">{p.code}</span>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[11px] text-slate-500 font-mono w-10 text-right">{p.count}</span>
                  </div>
                );
              })}
              {(!stats.pays_top || stats.pays_top.length === 0) && (
                <p className="text-xs text-slate-400 italic">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tendance 7j vs 7j précédent */}
        <Card className="border-slate-200 overflow-hidden" data-testid="kpi-trend-7d">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                7j vs 7j
              </span>
            </div>
            <div className={`text-3xl font-bold ${trendColor}`} style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {trend}
            </div>
            <p className="text-xs text-slate-500 mt-1">Tendance hebdomadaire</p>
            <p className="text-[11px] text-slate-400 mt-2">
              {last7} cette sem · {prev7} sem. précédente
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Courbe 30 jours */}
      <Card className="border-slate-200" data-testid="partenariat-chart-30d">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                Pipeline · 30 derniers jours
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Identifier les périodes creuses et les pics de demande
              </p>
            </div>
            <span className="text-[11px] text-slate-400 font-mono">
              max/jour : {maxDaily}
            </span>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <AreaChart
                data={(stats.daily_30d || []).map(d => ({ ...d, label: formatShortDate(d.date) }))}
                margin={{ top: 8, right: 12, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="partenariat-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                  labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                  formatter={(v) => [`${v} demande${v > 1 ? 's' : ''}`, 'Reçues']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fill="url(#partenariat-grad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
